import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import type { SortingState } from '@/components/shared/DataTable/types';
import {
  getTablePrefs,
  type TablePrefs,
  useRestorePrefsToSearch,
  useTablePreferences,
} from './useTablePreferences';

export interface UseTableOptions {
  /**
   * Remap DataTable column IDs → API sort field names.
   * e.g. { retracedRiskScore: 'riskScore' }
   */
  sortKeyMap?: Record<string, string>;
  /**
   * When set, restore-from-prefs is skipped unless the active tab matches.
   */
  tab?: string;
  /**
   * Sort applied when neither the URL/local state nor saved prefs specify one.
   * Format: `field:ASC` / `field:DESC` (the API sort-field name, already mapped).
   * Lets a table load pre-sorted without the user clicking a header.
   */
  defaultSort?: string;
  /**
   * Where to keep table state.
   *  - `'url'` *(default)* — page/limit/sort/filters live in the route's
   *    search params; deep-linkable and survive refresh.
   *  - `'state'` — local React state on the hook caller; URL stays clean.
   *    Use for embedded / secondary tables (e.g. version-history below the
   *    audit info card, related-actions inside a sheet).
   */
  mode?: 'url' | 'state';
  /** Initial values when `mode === 'state'`. Ignored in URL mode. */
  initialState?: {
    page?: number;
    limit?: number;
    sort?: string;
    search?: string;
    filters?: Record<string, string[]>;
  };
  /** Route-specific search params (e.g. `'archived'`) whose presence also skips
   *  restore-from-prefs — a shared deep link must not gain the recipient's saved filters. */
  extraSearchKeys?: string[];
}

interface AnySearch {
  tab?: string;
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
  filters?: Record<string, string[]>;
}

function buildFilterParam(filters: Record<string, string[]>): string {
  const out: Record<string, string> = {};
  for (const [key, vals] of Object.entries(filters)) {
    if (vals && vals.length > 0) out[key] = vals.join(',');
  }
  return JSON.stringify(out);
}

/** Saved sorting → the `field:DIR` string this hook keeps in the URL/state. */
function savedSortString(
  prefs: TablePrefs,
  sortKeyMap: Record<string, string>,
): string | undefined {
  if (!prefs.sorting) return undefined;
  try {
    const first = (JSON.parse(prefs.sorting) as SortingState)[0];
    if (!first) return undefined;
    return `${sortKeyMap[first.id] ?? first.id}:${first.desc ? 'DESC' : 'ASC'}`;
  } catch {
    return undefined;
  }
}

/** Saved filters → the filter map this hook keeps in the URL/state. */
function savedFilters(prefs: TablePrefs): Record<string, string[]> | undefined {
  if (!prefs.filters) return undefined;
  try {
    return JSON.parse(prefs.filters) as Record<string, string[]>;
  } catch {
    return undefined;
  }
}

export function useTable(storageKey: string, opts: UseTableOptions = {}) {
  const {
    sortKeyMap = {},
    tab,
    defaultSort,
    mode = 'url',
    initialState = {},
    extraSearchKeys = [],
  } = opts;

  // --- Local-state mode ------------------------------------------------------
  // Self-contained: no URL, no prefs restore. Caller's component owns the
  // state; sibling tables on the same route stay independent.
  // Seeded from the account for the same reason `useRestorePrefsToSearch` seeds
  // the URL in the other mode — a table the user has arranged should come back
  // arranged, whether or not it puts its state in the address bar. The caller's
  // own `initialState` still wins: that is a deliberate default, not a leftover.
  const savedPrefs = getTablePrefs(storageKey);
  const [localPage, setLocalPage] = useState(initialState.page ?? 1);
  const [localLimit, setLocalLimit] = useState(
    initialState.limit ?? savedPrefs.rowsPerPage ?? 50,
  );
  const [localSort, setLocalSort] = useState<string | undefined>(
    initialState.sort ?? savedSortString(savedPrefs, opts.sortKeyMap ?? {}),
  );
  const [localSearch, setLocalSearch] = useState(initialState.search ?? '');
  const [localFilters, setLocalFilters] = useState<Record<string, string[]>>(
    initialState.filters ?? savedFilters(savedPrefs) ?? {},
  );

  // --- URL-mode plumbing -----------------------------------------------------
  const routeSearch = (useSearch({ strict: false }) ?? {}) as AnySearch;
  const navigate = useNavigate();
  const { save: savePrefs } = useTablePreferences(storageKey);

  const hasExplicitTableSearch =
    routeSearch.sort !== undefined ||
    routeSearch.filters !== undefined ||
    routeSearch.search !== undefined ||
    extraSearchKeys.some(
      key => (routeSearch as Record<string, unknown>)[key] !== undefined,
    );
  const hasExistingSearch = tab
    ? (routeSearch.tab !== undefined && routeSearch.tab !== tab) ||
      hasExplicitTableSearch
    : hasExplicitTableSearch;

  useRestorePrefsToSearch(storageKey, {
    hasExistingSearch,
    enabled: mode === 'url',
    mapPrefs: prefs => {
      const sortStr = savedSortString(prefs, sortKeyMap);
      const filters = savedFilters(prefs);
      return {
        ...(sortStr !== undefined ? { sort: sortStr } : {}),
        ...(filters !== undefined ? { filters } : {}),
      };
    },
  });

  const isActiveTab =
    !tab || routeSearch.tab === undefined || routeSearch.tab === tab;

  // --- Derived state ---------------------------------------------------------
  const page =
    mode === 'state' ? localPage : isActiveTab ? (routeSearch.page ?? 1) : 1;
  const limit =
    mode === 'state'
      ? localLimit
      : isActiveTab
        ? (routeSearch.limit ?? 50)
        : 50;
  const search = mode === 'state' ? localSearch : (routeSearch.search ?? '');
  const filters: Record<string, string[]> =
    mode === 'state'
      ? localFilters
      : ((isActiveTab ? (routeSearch.filters ?? {}) : {}) as Record<
          string,
          string[]
        >);
  const activeSort = mode === 'state' ? localSort : routeSearch.sort;
  // Fall back to the caller's default sort when nothing explicit is set, so the
  // table loads pre-sorted and the query sends a `sort` param on first render.
  const sortStr = activeSort ?? defaultSort;

  const sorting: SortingState = useMemo(() => {
    const s = mode === 'state' ? sortStr : isActiveTab ? sortStr : undefined;
    if (!s) return [];
    const [field, dir] = s.split(':');
    return field ? [{ id: field, desc: dir === 'DESC' }] : [];
  }, [mode, isActiveTab, sortStr]);

  // navigate is called from generic hooks that don't know the route's search schema.
  const nav = navigate as unknown as (opts: {
    search: (prev: any) => any;
  }) => void;

  function onSortingChange(next: SortingState) {
    const first = next[0];
    const nextSort = first
      ? `${sortKeyMap[first.id] ?? first.id}:${first.desc ? 'DESC' : 'ASC'}`
      : undefined;
    if (mode === 'state') {
      setLocalSort(nextSort);
      setLocalPage(1);
      savePrefs({ sorting: first ? JSON.stringify(next) : undefined });
      return;
    }
    if (!first) {
      nav({ search: prev => ({ ...prev, page: 1, sort: undefined }) });
      savePrefs({ sorting: undefined });
      return;
    }
    nav({ search: prev => ({ ...prev, page: 1, sort: nextSort }) });
    savePrefs({ sorting: JSON.stringify(next) });
  }

  function onFiltersChange(next: Record<string, string[]>) {
    const isEmpty = Object.values(next).every(v => !v || v.length === 0);
    if (mode === 'state') {
      setLocalFilters(isEmpty ? {} : next);
      setLocalPage(1);
      savePrefs({ filters: isEmpty ? undefined : JSON.stringify(next) });
      return;
    }
    nav({
      search: prev => ({
        ...prev,
        page: 1,
        filters: isEmpty ? undefined : next,
      }),
    });
    savePrefs({ filters: isEmpty ? undefined : JSON.stringify(next) });
  }

  function onPaginationChange(v: { pageIndex: number; pageSize: number }) {
    if (mode === 'state') {
      setLocalPage(v.pageIndex + 1);
      setLocalLimit(v.pageSize);
      savePrefs({ rowsPerPage: v.pageSize });
      return;
    }
    nav({
      search: prev => ({ ...prev, page: v.pageIndex + 1, limit: v.pageSize }),
    });
    savePrefs({ rowsPerPage: v.pageSize });
  }

  function onSearchChange(next: string) {
    if (mode === 'state') {
      setLocalSearch(next);
      setLocalPage(1);
      return;
    }
    nav({ search: prev => ({ ...prev, search: next || undefined, page: 1 }) });
  }

  /** Restores every state slot to its initial value — for dialog-embedded
   *  state-mode tables that must come up clean on reopen. In URL mode it
   *  clears the table params from the route search instead.
   *
   *  The saved sort and filters are dropped along with the live ones: now that
   *  a state-mode table restores what it saved, leaving them behind would have
   *  the next open come up with exactly what `reset` was called to clear. */
  function reset() {
    savePrefs({ sorting: undefined, filters: undefined });
    if (mode === 'state') {
      setLocalPage(initialState.page ?? 1);
      setLocalLimit(initialState.limit ?? 50);
      setLocalSort(initialState.sort);
      setLocalSearch(initialState.search ?? '');
      setLocalFilters(initialState.filters ?? {});
      return;
    }
    nav({
      search: prev => ({
        ...prev,
        page: undefined,
        limit: undefined,
        sort: undefined,
        search: undefined,
        filters: undefined,
      }),
    });
  }

  const filterParam =
    Object.keys(filters).length > 0 ? buildFilterParam(filters) : undefined;

  // Ready-to-spread into any data hook. Both `limit` and `count` are included
  // because different APIs use different names for page size.
  const queryParams = {
    page,
    limit,
    count: limit,
    ...(sortStr !== undefined ? { sort: sortStr } : {}),
    ...(filterParam !== undefined ? { filter: filterParam } : {}),
    ...(search ? { search } : {}),
  };

  return {
    /** The key these preferences are saved under — pass it to the table's
     *  `storageKey` so the two halves cannot drift apart. */
    storageKey,
    sorting,
    filters,
    search,
    page,
    limit,
    onSortingChange,
    onFiltersChange,
    onPaginationChange,
    onSearchChange,
    reset,
    queryParams,
  };
}
